import type { ReactNode } from 'react';
import type { NavigateFunction } from 'react-router';
import { useMemo, useState, useRef } from 'react';
import { Container, Drawer, Stack } from '@workramp/ui';
import { useTheme } from 'styled-components';
import WorkRampApps from '~/navigation/workramp-apps';
import { useMediaQuery } from '~/hooks';
import { PreviewDrawer } from '~/features/common';
import { useReviewerAssignmentsCountQuery } from '~/elc/features/challenge-reviewer-dashboard/api';
import { EnterpriseColorProvider } from '~/providers/EnterpriseColorProvider';
import { AppState } from '~/common/explicit-exports';
import { generateSidebarItems } from '../../utils';
import { SIDEBAR_WIDTH, LEARNER_ROUTES, PLACEHOLDER_LOGO_URL } from '../../constants';
import { Header } from '../Header';
import { Sidebar } from '../Sidebar';
import { Wrapper, Aside, Body, PositionWrapper, MobilePadding } from './LearnerShell.styles';

export interface LearnerShellProps {
  children: ReactNode;
  authToken: string;
  searchBar?: ReactNode;
  header?: ReactNode;
  navigate?: NavigateFunction;
}

export function LearnerShell({ children, authToken, searchBar, header, navigate }: LearnerShellProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const theme = useTheme();
  const isMdAndUp = useMediaQuery(theme.breakpoints.up('md'));
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const reviewerAssignmentsCountQuery = useReviewerAssignmentsCountQuery();
  const assignmentsCount = reviewerAssignmentsCountQuery.data?.count ?? 0;

  const navItems = useMemo(() => generateSidebarItems(assignmentsCount, navigate), [assignmentsCount, navigate]);

  const user = window.getCurrentUser();

  const { display_name: enterpriseName, scheme_color: brandColor, showColorLogo, colorLogo } = user.enterprise;
  const logoUrl = showColorLogo && colorLogo ? colorLogo : PLACEHOLDER_LOGO_URL;

  const { isWhiteLabel } = AppState;
  // Show/hide admin console link
  const showAdminConsole = !isWhiteLabel && WorkRampApps.getAdminApps(user).length > 0;

  const handleLogout = () => {
    formRef.current?.submit();
  };

  const handleOpenSidebar = () => {
    setIsSidebarOpen(true);
  };

  const handleCloseSidebar = () => {
    setIsSidebarOpen(false);
  };

  const sidebarContent = (
    <Sidebar
      userId={user.id}
      enterpriseName={enterpriseName}
      brandColor={brandColor}
      logoUrl={logoUrl}
      items={navItems}
    />
  );

  return (
    <EnterpriseColorProvider>
      <PreviewDrawer>
        <Wrapper>
          {!isWhiteLabel && isMdAndUp && <Aside>{sidebarContent}</Aside>}
          {!isWhiteLabel && !isMdAndUp && (
            <Drawer width={`${SIDEBAR_WIDTH}px`} isOpen={isSidebarOpen} onClose={handleCloseSidebar} position="left">
              {sidebarContent}
            </Drawer>
          )}
          <MobilePadding />
          <Body>
            <Stack size={2}>
              <Header
                userId={user.id}
                brandColor={brandColor}
                showAdminConsole={showAdminConsole}
                isWhiteLabel={isWhiteLabel}
                onLogout={handleLogout}
                onOpenMenu={handleOpenSidebar}
                showMenuButton={!isMdAndUp}
                searchBar={searchBar}
              />

              {header}
            </Stack>

            <Container isFluid compression={!isWhiteLabel && isMdAndUp ? SIDEBAR_WIDTH : 0}>
              <PositionWrapper>{children}</PositionWrapper>
            </Container>
          </Body>

          <form ref={formRef} action={LEARNER_ROUTES.logout} method="POST">
            <input type="hidden" name="_method" value="DELETE" />
            <input name="authenticity_token" type="hidden" value={authToken} />
          </form>
        </Wrapper>
      </PreviewDrawer>
    </EnterpriseColorProvider>
  );
}
